import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import DashboardNav from './DashboardNav';
import { useTheme } from '../../contexts/ThemeContext';
import { useHeartbeat } from '../../lib/useHeartbeat';
import { useMessageNotifications } from '../../hooks/useMessageNotifications';
import { NotificationProvider, useNotifications } from '../../contexts/NotificationContext';
import NotifPanel from './NotifPanel';
import CreditsDisplay from '../Credits/CreditsDisplay';
import RadioPlayer from './RadioPlayer';
import ParticleBackground from './ParticleBackground';
import { TaskbarProvider } from '../../contexts/TaskbarContext';
import Taskbar from '../Taskbar/Taskbar';

const breadcrumbMap = {
  'jobs':        'Jobs',
  'messages':    'Messages',
  'profile':     'Profile',
  'files':       'Files',
  'approvals':   'Approvals',
  'sales-rep':   'My Sales Rep',
  'pipeline':    'Lead Pipeline',
  'clients':     'My Clients',
  'referrals':   'Referral Links',
  'commissions': 'Commissions',
  'submissions': 'Submissions',
  'payouts':     'Payouts',
  'users':       'Users',
  'invites':     'Invites',
  'reports':     'Reports',
  'services':    'Services',
  'requests':    'Requests',
  'billing':     'Billing',
  'ai-prospects': 'AI Prospects',
  'data-approvals': 'Data Approvals',
  'bug-reports':              'Bug Reports',
  'promo-codes':              'Promo Codes',
  'contractor-applications':  'Contractor Applications',
  'sms-calendar':   'SMS Calendar',
  'credit-manager': 'Credit Manager',
  'live-chats':     'Live Chats',
  'subscriptions':  'Subscriptions',
  'offers':         'Custom Offers',
  'profitability':  'Profitability',
  'projects':       'Projects',
  'contractor-admin': 'Contractors Admin',
  'contractors':    'Contractors',
  'manage-contractors': 'Manage Contractors',
};

const roleTitles = {
  admin:             'Admin',
  sales_rep:         'Sales Rep',
  client:            'Client',
  contractor:        'Contractor',
  contractor_admin:  'Contractors Admin',
};

function timeAgo(d) {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

const ROLE_COLORS = {
  admin:      'bg-purple-500/20 text-purple-400',
  sales_rep:  'bg-yellow-500/20 text-yellow-400',
  client:     'bg-green-500/20 text-green-400',
  contractor: 'bg-cyan-500/20 text-cyan-400',
};

function MessageDropdown({ conversations, unreadCount, onMarkAllRead, onNavigate, role }) {
  const messagesPath = `${
    role === 'sales_rep' ? '/dashboard/sales' :
    role === 'admin'     ? '/dashboard/admin' :
    role === 'client'    ? '/dashboard/client' :
                           '/dashboard/contractor'
  }/messages`;

  return (
    <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-card border border-theme rounded-2xl shadow-2xl z-50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-theme">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-primary">Messages</span>
          {unreadCount > 0 && (
            <span className="text-xs bg-yellow-500 text-black font-bold px-2 py-0.5 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              className="text-xs text-yellow-500 hover:text-yellow-400 transition-colors font-medium"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={() => onNavigate(messagesPath)}
            className="text-xs text-secondary hover:text-primary transition-colors"
          >
            See all →
          </button>
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <div className="w-12 h-12 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-primary">You're all caught up</p>
            <p className="text-xs text-muted mt-1">No new messages</p>
          </div>
        ) : (
          <div>
            {conversations.map(conv => {
              const sender = conv.latestMsg?.sender;
              const initial = (sender?.full_name || '?')[0].toUpperCase();
              const roleColor = ROLE_COLORS[sender?.role] || ROLE_COLORS.client;

              return (
                <button
                  key={conv.key}
                  onClick={() => onNavigate(conv.navTo, conv.navState)}
                  className="w-full flex items-start gap-3 px-4 py-3 hover:bg-card-hover transition-colors text-left border-b border-theme/50 last:border-0"
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold border ${roleColor} border-current/30`}>
                    {initial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="text-xs font-semibold text-primary truncate">
                        {conv.title}
                      </span>
                      <span className="text-[10px] text-muted flex-shrink-0">
                        {timeAgo(conv.latestMsg?.created_at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-[10px] text-secondary font-medium">
                        {sender?.full_name || 'Someone'}
                      </span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${roleColor}`}>
                        {sender?.role?.replace('_', ' ') || ''}
                      </span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                        conv.ctxType === 'job' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-cyan-500/10 text-cyan-500'
                      }`}>
                        {conv.ctxType === 'job' ? 'Job' : 'Request'}
                      </span>
                    </div>
                    <p className="text-xs text-muted truncate leading-relaxed">
                      {conv.latestMsg?.content}
                    </p>
                  </div>
                  {conv.count > 1 && (
                    <span className="flex-shrink-0 mt-1 text-[10px] bg-yellow-500 text-black font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                      {conv.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {conversations.length > 0 && (
        <div className="border-t border-theme px-4 py-2.5">
          <button
            onClick={() => onNavigate(messagesPath)}
            className="w-full text-center text-xs text-yellow-500 hover:text-yellow-400 font-medium transition-colors py-1"
          >
            Open full inbox →
          </button>
        </div>
      )}
    </div>
  );
}

function DashboardLayoutInner({ user, profile, onSignOut }) {
  const [sidebarOpen, setSidebarOpen]       = useState(false);
  const [sidebarAnimating, setSidebarAnimating] = useState(false);
  const [msgOpen, setMsgOpen]               = useState(false);
  const [notifOpen, setNotifOpen]           = useState(false);
  const msgRef                              = useRef(null);
  const notifRef                            = useRef(null);
  const navigate                            = useNavigate();
  const location                            = useLocation();
  const role                                = profile?.role || 'client';
  const { toggleTheme, isDark }             = useTheme();
  const { conversations, unreadCount, markAllRead } = useMessageNotifications();
  const { count: notifCount } = useNotifications();
  useHeartbeat();

  const handleSignOut = async () => {
    if (onSignOut) await onSignOut();
    navigate('/login');
  };

  const openSidebar = () => {
    setSidebarOpen(true);
    setSidebarAnimating(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setSidebarAnimating(false)));
  };

  const closeSidebar = () => {
    setSidebarAnimating(true);
    setTimeout(() => { setSidebarOpen(false); setSidebarAnimating(false); }, 300);
  };

  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  useEffect(() => { closeSidebar(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.pathname.includes('/messages')) {
      markAllRead();
      setMsgOpen(false);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!msgOpen && !notifOpen) return;
    const handler = (e) => {
      if (msgRef.current && !msgRef.current.contains(e.target)) setMsgOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [msgOpen, notifOpen]);

  const handleMsgNavigate = (path, state) => {
    setMsgOpen(false);
    if (state) navigate(path, { state });
    else navigate(path);
  };

  const basePath = `/dashboard/${role === 'sales_rep' ? 'sales' : role}`;
  const pathSegments = location.pathname.replace(basePath, '').split('/').filter(Boolean);
  const currentPage = pathSegments.length > 0 ? breadcrumbMap[pathSegments[0]] || pathSegments[0] : 'Dashboard';
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const showBackBtn = pathSegments.length > 0 && (isIOS || window.history.length > 1);

  return (
    <div className="min-h-screen bg-surface flex" style={{ position: 'relative' }}>
      <ParticleBackground />
      <aside className="hidden lg:flex lg:flex-col lg:w-[260px] lg:fixed lg:inset-y-0 bg-card border-r border-theme z-40">
        <DashboardNav
          role={role}
          collapsed={false}
          onClose={() => {}}
          user={user}
          profile={profile}
          showUserCard={true}
          onSignOut={handleSignOut}
        />
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${sidebarAnimating ? 'opacity-0' : 'opacity-100'}`}
            onClick={closeSidebar}
          />
          <div
            className={`fixed inset-y-0 left-0 w-[280px] bg-card border-r border-theme z-50 transition-transform duration-300 ease-out shadow-2xl ${sidebarAnimating ? '-translate-x-full' : 'translate-x-0'}`}
          >
            <DashboardNav
              role={role}
              collapsed={false}
              onClose={closeSidebar}
              user={user}
              profile={profile}
              showUserCard={true}
              onSignOut={handleSignOut}
            />
          </div>
        </div>
      )}

      <div className="flex-1 lg:pl-[260px] flex flex-col min-h-screen min-w-0 overflow-x-hidden">
        <header className="sticky top-0 z-30 bg-card/95 backdrop-blur-md border-b border-theme">
          <div className="flex items-center justify-between px-4 py-3 sm:px-6">

            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={openSidebar}
                className="lg:hidden text-secondary hover:text-primary min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-surface transition-colors flex-shrink-0"
                aria-label="Open navigation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              {showBackBtn && (
                <button
                  onClick={() => navigate(-1)}
                  className="lg:hidden text-secondary hover:text-primary min-w-[36px] min-h-[36px] flex items-center justify-center rounded-lg hover:bg-surface transition-colors flex-shrink-0"
                  aria-label="Go back"
                  title="Back"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="hidden sm:flex items-center gap-2 text-sm min-w-0">
                <span className="text-muted flex-shrink-0">{roleTitles[role] || role}</span>
                <svg className="w-3.5 h-3.5 text-muted flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className="font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{currentPage}</span>
              </div>
              <span className="sm:hidden font-semibold text-base truncate max-w-[140px]" style={{ color: 'var(--color-text-primary)' }}>{currentPage}</span>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2">
              {(role === 'sales_rep' || role === 'admin') && (
                <>
                  <RadioPlayer />
                  <div className="hidden sm:flex items-center gap-2">
                    <div className="w-px h-5 bg-white/10 flex-shrink-0" />
                    <CreditsDisplay />
                  </div>
                </>
              )}
              <button
                onClick={toggleTheme}
                className="text-secondary hover:text-primary transition-colors w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface"
                title={isDark ? 'Light Mode' : 'Dark Mode'}
              >
                {isDark ? (
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* ── System Notification Bell ─────────────────────── */}
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => { setNotifOpen(prev => !prev); if (msgOpen) setMsgOpen(false); }}
                  className={`relative text-secondary hover:text-primary transition-colors w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface ${notifOpen ? 'bg-surface text-primary' : ''}`}
                  title="Notifications"
                >
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {notifCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-card leading-none">
                      {notifCount > 99 ? '99+' : notifCount}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-[380px] bg-card border border-theme rounded-2xl shadow-2xl z-50 overflow-hidden">
                    <NotifPanel
                      role={role}
                      navigate={navigate}
                      onClose={() => setNotifOpen(false)}
                    />
                  </div>
                )}
              </div>

              {/* ── Message Notification Bell ───────────────────────── */}
              <div className="relative" ref={msgRef}>
                <button
                  onClick={() => setMsgOpen(prev => !prev)}
                  className={`relative text-secondary hover:text-primary transition-colors w-9 h-9 flex items-center justify-center rounded-lg hover:bg-surface ${msgOpen ? 'bg-surface text-primary' : ''}`}
                  title="Messages"
                >
                  <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-yellow-500 text-black text-[9px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-card leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>

                {msgOpen && (
                  <MessageDropdown
                    conversations={conversations}
                    unreadCount={unreadCount}
                    onMarkAllRead={() => { markAllRead(); setMsgOpen(false); }}
                    onNavigate={handleMsgNavigate}
                    role={role}
                  />
                )}
              </div>

              <div className="hidden sm:flex items-center gap-2.5 pl-2 border-l border-theme">
                <div className="w-8 h-8 rounded-full bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-yellow-500 text-xs font-bold">
                    {profile?.full_name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="leading-tight">
                  <p className="text-sm font-medium leading-none" style={{ color: 'var(--color-text-primary)' }}>
                    {profile?.full_name || user?.email || 'User'}
                  </p>
                  <p className="text-xs text-muted mt-0.5 capitalize">
                    {roleTitles[role] || role}
                  </p>
                </div>
              </div>

              <button
                onClick={handleSignOut}
                className="text-muted hover:text-red-400 transition-colors w-9 h-9 flex items-center justify-center rounded-lg hover:bg-red-500/5"
                title="Sign Out"
              >
                <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto min-w-0" style={{ paddingBottom: 'calc(52px + max(1rem, env(safe-area-inset-bottom)))' }}>
          <Outlet />
        </main>
        <Taskbar role={role} />
      </div>
    </div>
  );
}

function DashboardLayout({ user, profile, onSignOut }) {
  return (
    <NotificationProvider user={user}>
      <TaskbarProvider>
        <DashboardLayoutInner user={user} profile={profile} onSignOut={onSignOut} />
      </TaskbarProvider>
    </NotificationProvider>
  );
}

export default DashboardLayout;
